<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<!-- eslint-disable vue/no-v-html -->
<template>
  <article class="sam-data-table">
    <div
      v-if="$slots.header"
      class="sam-data-table__header"
    >
      <slot name="header" />
    </div>
    <div
      ref="dataTableWrapper"
      class="sam-data-table__table-wrapper"
    >
      <table
        ref="dataTable"
        class="sam-data-table__table"
      >
        <thead
          ref="dataTableHeader"
          :class="['sam-data-table__table-header', { '--sticky': stickyHeaders }]"
        >
          <tr class="sam-data-table__table-header-row">
            <th
              v-if="hasSelection"
              class="sam-data-table__table-header-th --selection"
            >
              <SamCheckbox
                :data="loading ? false : isEveryItemSelected"
                class="sam-data-table__row-checkbox"
                :disabled="loading"
                @change="toggleAllSelection"
              />
            </th>
            <template v-for="(column, index) in columns">
              <th
                :key="`data-table--header-${index}`"
                :class="[
                  `sam-data-table__table-header-th`,
                  { 'is-cell-disabled': column.disableCell },
                  { 'is-column-disabled': column.disableColumn },
                  column.samStyle,
                ]"
                @click="onTableHeaderClick(column, $event)"
              >
                <template v-if="!!$slots.column || !!$scopedSlots.column">
                  <slot
                    name="column"
                    :column="column"
                    :index="index"
                  />
                </template>
                <template v-else>
                  {{ column.text }}
                </template>
              </th>
            </template>
          </tr>
        </thead>
        <tbody :class="['sam-data-table__table-body', { '--sticky': stickyHeaders }]">
          <template v-if="loading">
            <tr
              v-for="num in 3"
              :key="`data-table-loading-${num}`"
              class="sam-data-table__table-body-row"
            >
              <th
                v-if="hasSelection"
                class="sam-data-table__table-body-header --selection"
              >
                <SamCheckbox
                  :data="false"
                  disabled
                  class="sam-data-table__row-checkbox"
                />
              </th>
              <component
                :is="index === 0 ? 'th' : 'td'"
                v-for="(_, index) in columns"
                :key="`loading-cell-${num}-${index}`"
              >
                <span class="sam-data-table__loading-cell" />
              </component>
            </tr>
          </template>
          <tr v-else-if="!items.length && $slots['no-data']">
            <slot name="no-data" />
          </tr>
          <template v-else>
            <tr
              v-for="(item, index) in items"
              :key="`data-table--body-row-${index}`"
              class="sam-data-table__table-body-row"
            >
              <th
                v-if="hasSelection"
                class="sam-data-table__table-body-header --selection"
              >
                <SamCheckbox
                  :data="isItemSelected(item)"
                  :disabled="isItemDisabled(item)"
                  class="sam-data-table__row-checkbox"
                  @change="toggleItemSelection(item)"
                />
              </th>
              <slot
                name="row"
                :row="item"
                :index="index"
              />
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <div
      v-if="$slots.footer"
      class="sam-data-table__footer"
    >
      <slot name="footer" />
    </div>
  </article>
</template>

<script>
import SamCheckbox from '../SamCheckbox/SamCheckbox.vue';

export default {
  name: 'SamDataTable',
  components: { SamCheckbox },
  props: {
    columns: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    stickyHeaders: {
      type: Boolean,
      default: true,
    },
    tableHeight: {
      type: String,
      default: '80vh',
    },
    hasSelection: {
      type: Boolean,
      required: false,
      default: true,
    },
    itemSelectableProp: {
      type: String,
      required: false,
      default: 'selectable',
    },
    value: {
      type: Array,
      required: false,
      default: () => [],
    },
    itemValueProp: {
      type: String,
      required: false,
      default: 'value',
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({}),
  computed: {
    selection: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', [...val]);
        this.$emit('change', [...val]);
      },
    },
    isEveryItemSelected() {
      return this.items.every(this.isItemSelected);
    },
  },
  methods: {
    isItemSelected(item) {
      return this.value.includes(item[this.itemValueProp]);
    },
    isItemDisabled(item) {
      return !!item[this.itemSelectableProp];
    },
    onTableHeaderClick(column, $event) {
      this.$emit('table-header-click', {
        ...column,
        $event,
      });
    },
    toggleAllSelection() {
      if (this.isEveryItemSelected) this.selection = [];
      this.selection = this.items
        .filter((item) => !!item[this.itemSelectableProp])
        .map((item) => item[this.itemValueProp]);
    },
    toggleItemSelection(item) {
      if (this.isItemDisabled(item)) return;

      if (!this.isItemSelected(item)) {
        this.selection = [...this.selection, item[this.itemValueProp]];
      } else {
        const index = this.selection.indexOf(item[this.itemValueProp]);
        this.selection = [...this.selection].splice(index, 1);
      }
    },
  },
};
</script>

<style scoped lang="scss">
$data-table--border-style: $size--0--fourth solid $neutral--30;
$checkbox-th-width: 62px;

.sam-data-table {
  @extend .text--body-2;
  color: $neutral--90;
  background-color: $pure--white;
  border: $data-table--border-style;
  border-radius: $size--1;

  .sam-data-table__header,
  .sam-data-table__footer {
    @extend .px-6, .py-4;
  }

  .sam-data-table__table-wrapper {
    overflow: auto;
    position: relative;
    max-height: v-bind(tableHeight);

    &:not(:first-child) {
      border-top: $data-table--border-style;
    }
    &:not(:last-child) {
      border-bottom: $data-table--border-style;
    }

    table.sam-data-table__table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;

      // common row, table header and table data styles
      tr {
        &:not(:last-child) {
          td,
          th {
            border-bottom: $data-table--border-style;
          }
        }

        &:hover {
          background-color: #f9f9fe !important;
        }

        th,
        td {
          min-width: 182px;
          text-align: start;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;

          &:hover {
            background-color: $informative--surface !important;
          }

          ::v-deep {
            vertical-align: middle;
          }
        }
        .sam-data-table__loading-cell {
          border-radius: $size--2;
          height: $size--4;
          @include old-skeleton-blink;
          display: inline-block;
          width: 50%;
          vertical-align: middle;
        }
      }

      .is-column-disabled {
        color: $neutral--50;
        cursor: not-allowed !important;
        background-color: $informative--surface;
      }

      .warning {
        background-color: $warning--surface;
      }

      .secondary {
        background-color: $info-secondary--surface;
      }

      // table header specific styles
      thead {
        tr {
          th {
            @extend .text--subtitle, .p-4;
            text-align: start;
            background-color: $pure--white;
            border-bottom: $data-table--border-style;

            &:first-child {
              @extend .pl-6;
            }

            &:not(.--selection) {
              &.is-cell-disabled {
                color: $neutral--50;
                cursor: not-allowed;
              }
              cursor: pointer;
            }
          }
        }
        &.--sticky {
          position: sticky;
          inset-block-start: 0;
          z-index: 3;

          th:first-child {
            position: sticky;
            inset-inline-start: 0;
            border-right: $data-table--border-style;
            z-index: 3;
          }
          th.--selection + th {
            position: sticky;
            inset-inline-start: $checkbox-th-width;
            border-right: $data-table--border-style;
            z-index: 3;
          }
        }
      }

      // table body specific styles
      tbody {
        tr.sam-data-table__table-body-row {
          background-color: $pure--white;

          &:nth-child(odd) {
            background-color: #fdfdfd;
          }

          th {
            @extend .p-4, .text--body-2--medium;
            &:first-child {
              @extend .pl-6;
            }
          }
          td {
            @extend .text--body-2, .p-4;
            &:first-child {
              @extend .pl-6;
            }
            color: $neutral--80;
          }

          .sam-data-table__table-data.--empty {
            @extend .text--caption, .px-6, .py-4;
            text-align: center;
            font-style: italic;
          }
        }

        &.--sticky {
          ::v-deep {
            th:first-child,
            th.--selection + th {
              position: sticky;
              background-color: inherit;
              inset-inline-start: 0;
              border-right: $data-table--border-style;
              z-index: 2;
            }
            th.--selection + th {
              inset-inline-start: 62px;
            }
          }
        }
      }
    }
  }
  th.--selection {
    width: 62px !important;
    border-right: none !important;
    .sam-data-table__row-checkbox {
      vertical-align: middle;
      ::v-deep {
        .sam-checkbox__label {
          @extend .py-0;
        }
      }
    }
  }
}
</style>
